import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useAnalytics } from 'use-analytics';
import TipTapEditor from '../../EmailToClientForm/TipTapEditor';
import { showSpinner, richTextButtonStyles } from '../../EmailToClientForm';
import { useNLPConstants } from '../../ChatGPTInterface/constants/nlpSettings';
import extractBrochurePaperData from '../../EmailToClientForm/TipTapEditor/utils/extractBrochurePaperData';
import { translateTipTap } from '../utils/translateTipTap';
import { saveBrochureRequest, updateBrochureFields } from '../../../store/slices/brochure';
import { SearchStatus } from '../../../constants';
import useClipboard from '../../../hooks/useClipboard';
import generateBrochureLink from '../../../helpers/generateBrochureLink';
import useBrochureRateDisplayPreference from '../../../hooks/useBrochureRateDisplayPreference';
import { BrochureTemplateId } from '../../../constants';

const LANGUAGES = ['English', 'Portuguese', 'Brazilian Portuguese', 'Spanish', 'Polish'];

const PaperTemplate = ({ brochure, isLoggedIn }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const nlpConstants = useNLPConstants();
  const { roomDescriptions, hotelDetails: hotelDetailsStore } = useSelector((state) => state.hotel);
  const stateBrochure = useSelector((store) => store.brochure.brochure);
  const rateDisplayPreference = useBrochureRateDisplayPreference({ brochure, isBrochurePage });
  const { travelAgency } = useSelector((store) => store.user);
  const [isLinkCopied, copyLinkToClipboard] = useClipboard();
  const { saveBrochureStatus, saveBrochureError } = stateBrochure;

  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedJSON, setTranslatedJSON] = useState(null);
  const [regenerateKey, setRegenerateKey] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(
    isBrochurePage ? false : !brochure.isSavedToDatabase,
  );
  const editorInitializedRef = useRef(false);

  useEffect(() => {
    if (!stateBrochure.editorHTML) return;
    if (!editorInitializedRef.current) {
      editorInitializedRef.current = true;
      return;
    }
    setHasUnsavedChanges(true);
    // Intentionally omit hasUnsavedChanges from deps — including it caused the effect to re-run
    // when Save set it to false, immediately setting it back to true (stale closure loop).
    // oxlint-disable-next-line react/exhaustive-deps
  }, [stateBrochure.editorHTML]);

  const pageTitle = brochure.emailSubject || 'Itinerary';
  const isBrochureReady = isBrochurePage ? !!brochure.id : !!brochure.isSavedToDatabase;
  const brochureUrl = isBrochureReady
    ? generateBrochureLink(brochure.id, travelAgency, BrochureTemplateId.PAPER)
    : null;
  const isSaving = saveBrochureStatus === SearchStatus.SEARCHING;

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setSelectedLanguage(lang);
    if (lang === 'English') {
      setTranslatedJSON(null);
    }
  };

  const handleTranslate = async () => {
    setIsTranslating(true);
    track('PaperTemplate.translate', { language: selectedLanguage });
    try {
      const baseJSON = extractBrochurePaperData(
        brochure,
        roomDescriptions,
        hotelDetailsStore,
        rateDisplayPreference,
      );
      const translated = await translateTipTap(baseJSON, selectedLanguage, nlpConstants);
      setTranslatedJSON(translated);
      setHasUnsavedChanges(true);
    } catch (e) {
      console.error('Translation error:', e);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSave = () => {
    track('PaperTemplate.save', { brochureId: brochure.id });
    setHasUnsavedChanges(false);
    dispatch(
      updateBrochureFields({
        fields: {
          saveBrochureStatus: SearchStatus.SEARCHING,
          isSaveBrochureContent: true,
        },
      }),
    );
    dispatch(
      saveBrochureRequest({
        ...brochure,
        editorJSON: stateBrochure.editorJSON,
        editorHTML: stateBrochure.editorHTML,
        brochureId: brochure.id,
        savedBrochureId: brochure.id,
        doSaveToDatabase: true,
        doShowBrochureEditor: true,
      }),
    );
  };

  const handleRegenerate = () => {
    track('PaperTemplate.regenerate', { brochureId: brochure.id });
    setTranslatedJSON(null);
    setSelectedLanguage('English');
    setIsRegenerating(true);
    setRegenerateKey((k) => k + 1);
    setHasUnsavedChanges(true);
    // Reset so the next editorHTML update (fresh TipTap init) doesn't double-set hasUnsavedChanges
    editorInitializedRef.current = false;
  };

  return (
    <div className="min-h-screen brochure-tiptap-page" style={{ background: '#f3f4f6' }}>
      <div className="brochure-tiptap-outer">
        <header className="brochure-tiptap-header">
          <div className="flex items-center justify-between">
            <span className="brochure-tiptap-kicker">Your proposal</span>
            {isLoggedIn && (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {hasUnsavedChanges && !isSaving && (
                  <span className="text-xs font-medium bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full whitespace-nowrap">
                    Unsaved changes
                  </span>
                )}
                <select
                  value={selectedLanguage}
                  onChange={handleLanguageChange}
                  disabled={isTranslating}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
                {selectedLanguage !== 'English' && (
                  <button
                    type="button"
                    onClick={handleTranslate}
                    disabled={isTranslating}
                    className="text-sm px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isTranslating ? 'Translating…' : 'Translate'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleRegenerate}
                  className="text-sm px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                >
                  Reset
                </button>
                {brochureUrl && (
                  <>
                    {!isBrochurePage && (
                      <button
                        type="button"
                        onClick={() => {
                          window.open(brochureUrl, '_blank');
                          track('PaperTemplate.openBrochure', { brochureId: brochure.id });
                        }}
                        className="text-sm px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                      >
                        Open brochure
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        copyLinkToClipboard(brochureUrl);
                        track('PaperTemplate.copyLink', { brochureId: brochure.id });
                      }}
                      disabled={isLinkCopied}
                      className="text-sm px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap disabled:opacity-60"
                    >
                      {isLinkCopied ? 'Copied!' : 'Copy link'}
                    </button>
                  </>
                )}
                {saveBrochureError && (
                  <span className="text-sm text-red-600">{saveBrochureError}</span>
                )}
              </div>
            )}
          </div>
          <h1 className="brochure-tiptap-title">{pageTitle}</h1>
          <div className="brochure-tiptap-rule" />
        </header>
        <article className="brochure-tiptap-article">
          <TipTapEditor
            key={regenerateKey}
            brochure={brochure}
            isEditable={isLoggedIn}
            isBrochurePage={isBrochurePage}
            showSpinner={showSpinner}
            richTextButtonStyles={richTextButtonStyles}
            editorialLayout
            templateVariant="paper"
            overrideContent={translatedJSON}
            ignoreStoredContent={isRegenerating}
          />
        </article>
      </div>
    </div>
  );
};

PaperTemplate.propTypes = {
  brochure: PropTypes.shape({
    emailSubject: PropTypes.string,
    trips: PropTypes.objectOf(PropTypes.shape({})),
    id: PropTypes.string,
  }).isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
};

export default PaperTemplate;
